import { useEffect, useMemo, useRef, useState } from "react";

import { SiteFooter } from "@/components/SiteFooter";
import { SiteHeader } from "@/components/SiteHeader";
import { LP_CHROME_CSS } from "@/lib/lp-chrome-css";
import { HERNIA_GURGAON_CSS } from "@/lib/hernia-gurgaon-lp";
import { HERNIA_HOSPITAL_CSS } from "@/lib/hernia-hospital-gurgaon-lp";
import { HERNIA_TREATMENT_CSS } from "@/lib/hernia-treatment-gurgaon-lp";
import { herniaTreatmentHtml } from "@/lib/hernia-treatment-city-lp";
import { herniaHospitalHtml } from "@/lib/hernia-hospital-city-lp";
import { herniaCostHtml, type HerniaCostCity } from "@/lib/hernia-city-lp";
import { PILES_TREATMENT_CITIES } from "@/components/PilesTreatmentCityPage";
import { separateLandingPageDisclaimer } from "@/lib/landing-page-disclaimer";
import { notifyForm } from "@/lib/notifyForm";

function istHour(): number {
  try {
    return parseInt(
      new Date().toLocaleString("en-US", {
        timeZone: "Asia/Kolkata",
        hour: "2-digit",
        hour12: false,
      }),
      10,
    );
  } catch {
    return 12;
  }
}

export function HerniaSurgeryCostGurgaonPage({ city = "Gurgaon", hospital = false, treatment = false }: { city?: HerniaCostCity; hospital?: boolean; treatment?: boolean }) {
  const doctor = city === "Gurgaon" ? (hospital ? "Dr. N. K. Mandal" : "Dr. N.K. Mandal") : PILES_TREATMENT_CITIES[city].doctor;
  const rootRef = useRef<HTMLDivElement>(null);
  const [ctaMode, setCtaMode] = useState<"active" | "off">("active");
  const { contentHtml, disclaimerHtml } = useMemo(() => separateLandingPageDisclaimer(treatment ? herniaTreatmentHtml(city) : hospital ? herniaHospitalHtml(city) : herniaCostHtml(city)), [city, hospital, treatment]);
  useEffect(() => {
    const hour = istHour();
    setCtaMode(hour >= 9 && hour < 20 ? "active" : "off");
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const cleanups: Array<() => void> = [];
    const leadModal = root.querySelector<HTMLElement>("#lead-modal");
    const callModal = root.querySelector<HTMLElement>("#call-modal");
    const formContainer = root.querySelector<HTMLElement>("#modal-form-container");
    const thankYou = root.querySelector<HTMLElement>("#thank-you");

    const openModal = (modal: HTMLElement | null) => {
      if (!modal) return;
      modal.classList.add("open");
      modal.setAttribute("aria-hidden", "false");
      document.documentElement.classList.add("modal-open");
      const dialog = modal.querySelector<HTMLElement>(".modal-dialog");
      if (dialog) dialog.scrollTop = 0;
    };
    const closeModal = (modal: HTMLElement | null) => {
      if (!modal) return;
      modal.classList.remove("open");
      modal.setAttribute("aria-hidden", "true");
      if (!root.querySelector(".modal-backdrop.open")) {
        document.documentElement.classList.remove("modal-open");
      }
    };
    const openLeadForm = (selectedDoctor = doctor) => {
      const doctorField = root.querySelector<HTMLInputElement>("#m-doctor");
      const pageField = root.querySelector<HTMLInputElement>("#m-pageurl");
      if (doctorField) doctorField.value = selectedDoctor;
      if (pageField) pageField.value = window.location.href;
      if (formContainer) formContainer.style.display = "";
      thankYou?.classList.remove("active");
      openModal(leadModal);
      setTimeout(() => root.querySelector<HTMLInputElement>("#m-name")?.focus({ preventScroll: true }), 350);
    };

    const clickHandler = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const closeButton = target?.closest("[data-modal-close]");
      if (closeButton) {
        event.preventDefault();
        closeModal(closeButton.closest(".modal-backdrop") as HTMLElement);
        return;
      }
      const opener = target?.closest("[data-open-form]");
      if (opener) {
        event.preventDefault();
        openLeadForm(opener.getAttribute("data-doctor") || doctor);
      }
    };
    root.addEventListener("click", clickHandler);
    cleanups.push(() => root.removeEventListener("click", clickHandler));

    root.querySelectorAll<HTMLElement>(".modal-backdrop").forEach((backdrop) => {
      const handler = (event: Event) => {
        if (event.target === backdrop) closeModal(backdrop);
      };
      backdrop.addEventListener("click", handler);
      cleanups.push(() => backdrop.removeEventListener("click", handler));
    });

    const keyHandler = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        root.querySelectorAll<HTMLElement>(".modal-backdrop.open").forEach(closeModal);
      }
    };
    document.addEventListener("keydown", keyHandler);
    cleanups.push(() => document.removeEventListener("keydown", keyHandler));

    root.querySelectorAll<HTMLAnchorElement>('a[href^="tel:"]').forEach((link) => {
      const handler = (event: Event) => {
        if (root.dataset.ctaMode === "off") {
          event.preventDefault();
          openLeadForm();
          return;
        }
        if (window.matchMedia("(min-width: 900px) and (pointer: fine)").matches) {
          event.preventDefault();
          openModal(callModal);
        }
      };
      link.addEventListener("click", handler);
      cleanups.push(() => link.removeEventListener("click", handler));
    });

    const form = root.querySelector<HTMLFormElement>("#modal-form");
    if (form) {
      const handler = async (event: Event) => {
        event.preventDefault();
        if (form.getAttribute("aria-busy") === "true") return;
        const error = root.querySelector<HTMLElement>("#form-error");
        if (error) error.hidden = true;
        form.setAttribute("aria-busy", "true");
        const submit = form.querySelector<HTMLButtonElement>("[type=submit]");
        if (submit) submit.disabled = true;
        const data = new FormData(form);
        const name = String(data.get("name") ?? "").trim();
        const sent = await notifyForm({
          formType: treatment ? "Hernia Treatment Consultation" : hospital ? "Hernia Hospital Consultation" : "Hernia Surgery Cost Consultation",
          source: `${city} Hernia ${treatment ? "Treatment" : hospital ? "Hospital" : "Surgery Cost"} Page`,
          fields: [
            { label: "Name", value: name },
            { label: "Phone", value: String(data.get("phone") ?? "").trim() },
            { label: "Treatment", value: "Hernia Surgery" },
            { label: "City", value: city },
            { label: "Doctor", value: String(data.get("doctor") ?? "") },
            { label: "Page", value: window.location.href },
          ],
        });
        form.setAttribute("aria-busy", "false");
        if (submit) submit.disabled = false;
        if (!sent) {
          if (error) { error.hidden = false; error.textContent = "We couldn’t send your request. Please try again or call +91 95887 95887."; }
          return;
        }
        const thankYouName = root.querySelector("#ty-name");
        if (thankYouName) thankYouName.textContent = (name || "friend").split(" ")[0];
        form.reset();
        if (formContainer) formContainer.style.display = "none";
        thankYou?.classList.add("active");
        openModal(leadModal);
      };
      form.addEventListener("submit", handler);
      cleanups.push(() => form.removeEventListener("submit", handler));
    }

    const mapFrame = root.querySelector<HTMLElement>("#map-frame");
    if (mapFrame && "IntersectionObserver" in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          const src = mapFrame.dataset.mapSrc;
          if (src && !mapFrame.querySelector("iframe")) {
            const iframe = document.createElement("iframe");
            iframe.src = src;
            iframe.width = "100%";
            iframe.height = "100%";
            iframe.loading = "lazy";
            iframe.style.border = "0";
            iframe.title = `Healic Hospital ${city} — map`;
            iframe.referrerPolicy = "no-referrer-when-downgrade";
            mapFrame.appendChild(iframe);
          }
          observer.disconnect();
        },
        { rootMargin: "300px" },
      );
      observer.observe(mapFrame);
      cleanups.push(() => observer.disconnect());
    }

    return () => {
      cleanups.forEach((cleanup) => cleanup());
      document.documentElement.classList.remove("modal-open");
    };
  }, [city, doctor, hospital, treatment, ctaMode]);

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: (treatment ? HERNIA_TREATMENT_CSS : hospital ? HERNIA_HOSPITAL_CSS : HERNIA_GURGAON_CSS) + LP_CHROME_CSS }} />
      <div className="lp-chrome"><SiteHeader /></div>
      <div ref={rootRef} className={treatment ? "lp-hernia-treatment" : hospital ? "lp-hernia-hospital" : "lp-hernia-gurgaon"} data-cta-mode={ctaMode} dangerouslySetInnerHTML={{ __html: contentHtml }} />
      <div className="lp-chrome"><SiteFooter /></div>
      <div className={treatment ? "lp-hernia-treatment" : hospital ? "lp-hernia-hospital" : "lp-hernia-gurgaon"} dangerouslySetInnerHTML={{ __html: disclaimerHtml }} />
    </>
  );
}